import BaseView from './BaseView';
import { gsap } from "gsap";
import barba from '@barba/core';
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { SplitText } from "gsap/SplitText";

gsap.registerPlugin(ScrollToPlugin);

import ProductMediaCarousel from "../../components/ProductMediaCarousel";
import SizeGuideModal from "../../components/SizeGuideModal";
import ProductRelatedEditorial from '../../components/ProductRelatedEditorial';
import ProductEnlarger from "../../components/ProductEnlarger";
import Tabs from "../../components/Tabs";

let view = null;

class Product extends BaseView {
	constructor(container) {
		super(container);
	}

	init() {
		const tabsElm = this.container.querySelector('[data-product-tabs]');

		const logoLayerStalk = new CustomEvent('stalkElm', {
			detail: {
				element: tabsElm,
			},
		});

		document.dispatchEvent(logoLayerStalk);

		const groups = new Map([
			[ProductMediaCarousel, this.container.querySelectorAll('[data-media-wrapper]')],
			[ProductRelatedEditorial, this.container.querySelectorAll('[data-related-editorial]')]
		]);


		super.createModules(groups);


		// Observe Media
        this.observer = new IntersectionObserver(this.handleIntersection.bind(this), {
            rootMargin: '-49% 0% -49% 0%',
            treshold: 0
        });

		// Media
		this.mediaWrapper = this.container.querySelector('[data-media-wrapper]');
		this.carousel = this.mediaWrapper.querySelector('.m-product-media__wrapper');
		this.medias = this.container.querySelectorAll('[data-product-media]');


		this.mediaThumbs = this.container.querySelector('[data-product-media-thumbs]');
		this.thumbs = this.mediaThumbs.querySelectorAll('[data-product-media-thumb]');
		if(this.thumbs.length) {
			this.thumbs.forEach(thumb => {
				thumb.addEventListener('click', e => {

					const targetID = thumb.getAttribute('data-product-media-thumb');
					const targetMedia = this.mediaWrapper.querySelector('[data-product-media="'+targetID+'"]').querySelector('.c-img-video');

					gsap.to(window, {
						duration: 1,
						ease: 'power2.inOut',
						scrollTo: {
							y: targetMedia,
							offsetY: (window.innerHeight/2)-(targetMedia.scrollHeight/2)
						},
					});
				});
			});

			this.thumbIntro = gsap.timeline({
				paused: true,
			});

			this.thumbIntro.from(this.thumbs, {
				y: 100,
				scale: 0.5,

				ease: 'power2.inOut',
				duration: 1,
				stagger: 0.1,
				delay: 0
			}, 0);

			this.thumbIntro.to(this.thumbs, {
				opacity: 1,

				ease: 'power2.inOut',
				duration: 1,
				stagger: 0.1,
				delay: 0
			}, 0);
        }


		// Size Guide
		this.sizeGuideModal = this.container.querySelector('[data-size-guide-modal]');
		if (this.sizeGuideModal) {
			new SizeGuideModal(this.sizeGuideModal);
		}

		this.productInfo = this.container.querySelector('[data-product-info]');
		this.productTitle = this.container.querySelector('[data-product-title]');

		this.splitProductTitle = new SplitText(this.productTitle, {
            type: "words,chars",
            charsClass: "char"
        });


		// Product Content
		this.productContent = this.container.querySelector('[data-product-content]');
		this.resizeObserver = new ResizeObserver(entries => {
			this.setProductContentHeight();
		});
		this.resizeObserver.observe(this.productContent);
        this.setProductContentHeight();

		// Product Sticky
		this.productSticky = this.container.querySelector('[data-product-sticky]');
		this.resizeObserver2 = new ResizeObserver(entries => {
			this.setProductStickyHeight();
		});
		this.resizeObserver2.observe(this.productSticky);
        this.setProductStickyHeight();

		const mm = gsap.matchMedia();
		mm.add(
			{
				isMobile: `(max-width: 991px)`,
				isDesktop: `(min-width: 992px)`,
				reduceMotion: '(prefers-reduced-motion: reduce)',
			}, (context) => {
                this.context = context;

				gsap.to(this.productInfo, {
					y: this.context.conditions.isMobile ? 0 : 13,
					duration: 0.5,
					ease: 'none',
					scrollTrigger: {
						trigger: tabsElm,
						start: 'top bottom',
						end: '+=13',
						scrub: 0.3
					}
				});

				if(this.context.conditions.isMobile) {

					if(!this.productSticky) {
						return;
					}

					gsap.to(this.mediaWrapper, {
						opacity: 0.1,

						duration: 1,
						ease: 'power1.inOut',
						scrollTrigger: {
							trigger: this.productSticky,
							start: () => {
								return 'top '+(this.productSticky.getBoundingClientRect().top+window.scrollY);
							},
							end: 'top 65%',
							scrub: 1,
							onToggle: self => {
								if(self.progress>0) {
									this.mediaWrapper.classList.add('no-pointer-events');
								} else {
									this.mediaWrapper.classList.remove('no-pointer-events');
								}
							}
						}
					})
				} else {
					this.mediaWrapper.classList.remove('no-pointer-events');
				}
			}
		);


		// Product Enlarger
		this.productEnlargerElm = this.container.querySelector('[data-product-enlarger]');
		if(this.productEnlargerElm) {
			this.productEnlarger = new ProductEnlarger(this.productEnlargerElm);
		}

		this.medias.forEach(media => {
			media.addEventListener('click', e => {
				e.preventDefault();

				if(this.productEnlarger) {
					this.productEnlarger.activateImage(media);
				}
			});
		});


		// Tabs
		this.tabSection = this.container.querySelector('[data-tab-section]');
		if(this.tabSection) {
			this.tabs = new Tabs(this.tabSection);
		}


		// Questionmarks
		this.questionmarks = this.container.querySelectorAll('[data-questionmark]');

		this.questionmarks.forEach(questionmark => {
			questionmark.addEventListener('click', e => {
				e.preventDefault();

				this.target = questionmark.getAttribute('data-target');
				this.targetTabButton = document.getElementById(this.target);
				this.offset = '200';

				gsap.to(window, {
					duration: 1.5,
					ease: 'power2.inOut',
					scrollTo: {
						y: this.targetTabButton,
						offsetY: (window.innerHeight/2)-(this.targetTabButton.scrollHeight/2)
					},
				});

				if(this.tabs) {
					this.tabs.activateTab(this.targetTabButton);
				}
			});
		})

	}

	setProductContentHeight() {

		if(!this.medias.length) {
			return;
		}

		const firstMedia = this.medias[0].querySelector('.c-img-video');

		if(!firstMedia) {
			return;
		}

		let firstMediaHeight = firstMedia.getBoundingClientRect().height/2;
		if(this.context) {
			if(this.context.conditions.isMobile) {
				firstMediaHeight = this.carousel.getBoundingClientRect().height/2;
			}
		}

		let calculatedPaddingTop = '0rem';
		if(firstMediaHeight<window.innerHeight/2) {
			calculatedPaddingTop = 'calc(50svh - '+firstMediaHeight+'px)';
		}

		gsap.set(this.mediaWrapper, {
			paddingTop: calculatedPaddingTop
		});

		const productContentHeight = this.productContent.getBoundingClientRect().height;
		const calculatedTop = 'calc('+productContentHeight*-1+'px + 100vh)';
		gsap.set(this.productContent, {
			top: calculatedTop
		});

		ScrollTrigger.refresh(true);
	}

	setProductStickyHeight() {
		if(!this.productSticky) {
			return;
		}
		const stickyContentHeight = this.productSticky.getBoundingClientRect().height;

		const calculatedStickyTop = 'calc(100vh - '+stickyContentHeight+'px)';
		gsap.set(this.productSticky, {
			top: calculatedStickyTop
		});
	}

	afterEnterFunctions() {
		if(this.medias) {
            this.observeMedia(this.medias);
        }

		// Animate Title
		gsap.to(this.splitProductTitle.chars, {
			opacity: 1,
			duration: 0,
			stagger: 0.05,
			delay: 0
		});

		gsap.to(this.productInfo, {
			opacity: 1,

			duration: 1.2,
			ease: 'sine.inOut',
			delay: 1
		});

		if(this.thumbIntro) {
			this.thumbIntro.play();
		}
	}

	observeMedia(medias) {
		medias.forEach(media => {
			this.observer.observe(media);
		});
	}

	handleIntersection(entries) {
        entries.forEach(entry => {
			const ratio = entry.intersectionRatio;
			const target = entry.target;
			if (ratio > 0) { // Media is in center of viewport
				this.updateActiveMedia(target);
			}
		});
    }

	updateActiveMedia(media) {
		const id = media.dataset.productMedia;

		if(this.activeThumb) {
            this.activeThumb.classList.remove('is-active');
        }

		this.activeThumb = this.mediaThumbs.querySelector('[data-product-media-thumb="'+id+'"]');
		if(this.activeThumb) {
			this.activeThumb.classList.add('is-active');
		}

        const code = media.dataset.code;
        barba.logoLayerDirector.newCode(code);
    }

	destroy() {
		this.resizeObserver.disconnect();
		this.resizeObserver2.disconnect();
		this.observer.disconnect();

		this.productEnlarger.destroy();
	}
}

export default {
	namespace: 'single-wps_products',
	beforeEnter: ({ current, next, trigger }) => {
		view = new Product(next.container);
		view.init();
	},
	afterEnter: ({ current, next, trigger }) => {
		view.afterEnterFunctions();
	},
	afterLeave: ({ current, next, trigger }) => {
		view.destroy();
		view = null;
	},
};
